import * as THREE from "three";
import {
  fragment,
  vertex
} from "./shaders";

function GLManager(data) {
  this.totalEntries = data.length;
  this.loadedEntries = 0;
  const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 10000);
  camera.position.z = 5;

  const scene = new THREE.Scene();
  camera.lookAt = scene.position;

  const renderer = new THREE.WebGLRenderer({
    alpha: true,
    antialias: true
  });

  // if viewport has greater height/width ratio than the images, make canvas at least as tall as viewport
  if (window.outerHeight/document.body.clientWidth > 9/16) { 
    renderer.setSize(window.outerHeight * (16/9), window.outerHeight);

  } else { // make canvas at least as wide as viewport
    let newCanvasHeight = document.body.clientWidth*(9/16);
    renderer.setSize(document.body.clientWidth, newCanvasHeight);
    let heightDiff = (Math.abs(window.outerHeight - newCanvasHeight)) / 2;
    let newContainerHeight = window.outerHeight + heightDiff;
    document.getElementById('content-container').style.height = newContainerHeight + "px";
  }


  renderer.setPixelRatio(window.devicePixelRatio);
  renderer.setClearColor(0xffffff, 0.0);

  this.render = this.render.bind(this);

  if (window.isMobile) {
    this.textures = data.map((entry, i) =>
      new THREE.TextureLoader().load(
        entry.sImage,
        this.calculateAspectRatioFactor.bind(this, i)
      )
    );
    this.texturesInverse = data.map((entry, i) => 
      new THREE.TextureLoader().load(
        entry.sImageDW,
        this.calculateAspectRatioFactor.bind(this, i)
      )
    );
  } else {
    this.textures = data.map((entry, i) =>
      new THREE.TextureLoader().load(
        entry.image,
        this.calculateAspectRatioFactor.bind(this, i)
      )
    );
    this.texturesInverse = data.map((entry, i) => 
      new THREE.TextureLoader().load(
        entry.imageDW,
        this.calculateAspectRatioFactor.bind(this, i)
      )
    );
  }

  for (var i = 0; i < this.texturesInverse.length; i++) {
    var textureInverse = this.texturesInverse[i];
    textureInverse.magFilter = THREE.LinearFilter;
    textureInverse.minFilter = THREE.LinearFilter;
  }
  for (var j = 0; j < this.textures.length; j++) {
    var texture = this.textures[j];
    texture.magFilter = THREE.LinearFilter;
    texture.minFilter = THREE.LinearFilter;
  }
  
  this.texturesAlignment = data.map((entry, i) =>
    entry.imageAlign
  );
  
  this.factors = data.map(d => new THREE.Vector2(1, 1));
  this.currentIndex = 0;
  this.nextIndex = 0;
  this.textureProgress = 0;
  this.camera = camera;
  this.scene = scene;
  this.renderer = renderer;
  this.initialRender = false;
  this.time = 0;
  this.loopRaf = null;
  this.loop = this.loop.bind(this);
}
GLManager.prototype.getViewSize = function () {
  const fovInRadians = (this.camera.fov * Math.PI) / 180;
  const viewSize = Math.abs(
    this.camera.position.z * Math.tan(fovInRadians / 2) * 2
  );
  return viewSize;
};

GLManager.prototype.getPlaneSize = function () {
  const viewSize = this.getViewSize();
  return {
    width: viewSize,
    height: viewSize
  };
};
GLManager.prototype.calculateAspectRatioFactor = function (index, texture) {
  const plane = this.getPlaneSize();

  const windowRatio = document.body.clientWidth / window.outerHeight;
  const rectRatio = (plane.width / plane.height) * windowRatio;
  const imageRatio = texture.image.width / texture.image.height;

  let factorX = 1;
  let factorY = 1;
  this.factors[index] = new THREE.Vector2(factorX, factorY);

  if (this.currentIndex === index) {
    this.mesh.material.uniforms.u_textureFactor.value = this.factors[index];
    this.mesh.material.uniforms.u_textureFactor.needsUpdate = true;
  }
  if (this.nextIndex === index) {
    this.mesh.material.uniforms.u_texture2Factor.value = this.factors[index];
    this.mesh.material.uniforms.u_texture2Factor.needsUpdate = true;
  }
  if (this.initialRender) {
    this.loadedEntries++;
    if (this.loadedEntries === this.totalEntries) {
      document.body.classList.remove('loading');
    }
    this.render();
  }
};
// Plane Stuff
GLManager.prototype.createPlane = function () {
  // Calculate bas of Isoceles triangle(camera)
  const viewSize = this.getViewSize();
  const {
    width,
    height
  } = this.getPlaneSize();

  const segments = 60;
  const geometry = new THREE.PlaneBufferGeometry(
    width,
    height,
    segments,
    segments
  );

  const material = new THREE.ShaderMaterial({
    uniforms: {
      u_texture: {
        type: "t",
        value: this.textures[this.currentIndex]
      },
      u_textureFactor: {
        type: "f",
        value: this.factors[this.currentIndex]
      },
      u_texture2: {
        type: "t",
        value: this.textures[this.nextIndex]
      },
      u_texture2Factor: {
        type: "f",
        value: this.factors[this.nextIndex]
      },
      u_textureProgress: {
        type: "f",
        value: this.textureProgress
      },
      u_offset: {
        type: "f",
        value: 6
      },
      u_progress: {
        type: "f",
        value: 0
      },
      u_direction: {
        type: "f",
        value: 1
      },
      u_effect: {
        type: "f",
        value: 0
      },
      u_time: {
        type: "f",
        value: this.time
      },
      u_waveIntensity: {
        type: "f",
        value: 0
      },
      u_resolution: {
        type: "v2",
        value: new THREE.Vector2(document.body.clientWidth, window.outerHeight)
      },
      u_rgbPosition: {
        type: "v2",
        value: new THREE.Vector2(document.body.clientWidth / 2, window.outerHeight / 2)
      },
      u_rgbVelocity: {
        type: "v2",
        value: new THREE.Vector2(0, 0)
      },
      u_textureMappingPosition: {
        type: "i",
        value: this.texturesAlignment[this.currentIndex]
      }
    },
    vertexShader: vertex,
    fragmentShader: fragment,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 1.0
  });
  const mesh = new THREE.Mesh(geometry, material);
  this.scene.add(mesh);
  mesh.position.set(0,0,0);
  this.mesh = mesh;
};
GLManager.prototype.updateTexture = function (newIndex, progress) {
  let didChange = false;
  if (newIndex != null && this.newIndex !== this.currentIndex) {
    this.currentIndex = this.nextIndex;
    this.nextIndex = newIndex;
    this.textureProgress = 0;
    this.mesh.material.uniforms.u_textureProgress.value = 0;
    this.mesh.material.uniforms.u_texture.value = this.textures[
      this.currentIndex
    ];
    this.mesh.material.uniforms.u_textureFactor.value = this.factors[
      this.currentIndex
    ];
    this.mesh.material.uniforms.u_texture2.value = this.textures[newIndex];

    this.mesh.material.uniforms.u_texture2Factor.value = this.factors[newIndex];
    this.mesh.material.uniforms.u_textureMappingPosition.value = this.texturesAlignment[this.nextIndex];

    didChange = true;
  }
  if (progress != null && progress !== this.textureProgress) {
    this.mesh.material.uniforms.u_textureProgress.value = progress;
    this.textureProgress = progress;
    didChange = true;
  }

  if (!this.loopRaf && didChange) {
    this.render();
  }
};
GLManager.prototype.changeTextureMode = function(mode, progress, currentIndex) {
  let didChange = false;

  // go to DW
  if (mode === 0) {
    this.mesh.material.uniforms.u_textureProgress.value = progress;
    this.mesh.material.uniforms.u_texture.value = this.textures[currentIndex]
    this.mesh.material.uniforms.u_texture2.value = this.texturesInverse[
      currentIndex
    ];
  } else if (mode === 1) { // go back to NW
    this.textureProgress = 0;
    this.mesh.material.uniforms.u_textureProgress.value = progress;
    this.mesh.material.uniforms.u_texture.value = this.textures[
      currentIndex
    ];
  }

  didChange = true;

  if (!this.loopRaf && didChange) {
    this.render();
  }  
}
GLManager.prototype.updateStickEffect = function ({
  progress,
  direction,
  waveIntensity,
  directionX,
  directionY
}) {
  this.mesh.material.uniforms.u_progress.value = progress;
  this.mesh.material.uniforms.u_direction.value = direction;
  this.mesh.material.uniforms.u_waveIntensity.value = waveIntensity;

  this.scene.position.x = directionX;
  this.scene.position.y = directionY;
  
};

GLManager.prototype.updateRgbEffect = function ({
  position,
  velocity
}) {
  this.mesh.material.uniforms.u_rgbPosition.value = new THREE.Vector2(
    position.x,
    position.y
  );
  this.mesh.material.uniforms.u_rgbVelocity.value = new THREE.Vector2(
    velocity.x,
    velocity.y
  );

  if (!this.loopRaf) {
    this.render();
  }
};
// Other stuff
GLManager.prototype.render = function () {
  if (!this.initialRender) {
    this.initialRender = true;
  }
  this.renderer.render(this.scene, this.camera);
};
GLManager.prototype.mount = function (container) {
  container.appendChild(this.renderer.domElement);
};
GLManager.prototype.unmount = function () {
  this.mesh.material.dispose();
  this.mesh.geometry.dispose();
  this.mesh = null;
  this.renderer = null;
  this.camera = null;
  this.scene = null;
  this.container = null;
};
GLManager.prototype.onResize = function () {
  if (window.outerHeight/document.body.clientWidth > 9/16) { 
    
    let canvasWidth = window.outerHeight * (16/9);
    let windowWidth = document.body.clientWidth;
    let goldenValue = (canvasWidth - windowWidth) / (2 * windowWidth);
    
    let currentSlideIndex = parseInt(document.querySelector(".slide-index").innerHTML) - 1;

    if (currentSlideIndex === 1 || currentSlideIndex === 3) {
      this.scene.position.x = goldenValue
    } else if (currentSlideIndex === 2) {
      this.scene.position.x = -goldenValue;
    }

    this.renderer.setSize(window.outerHeight * (16/9), window.outerHeight);
  } else { // make canvas at least as wide as viewport
    this.scene.position.x = 0;
    let newCanvasHeight = document.body.clientWidth*(9/16);
    this.renderer.setSize(document.body.clientWidth, newCanvasHeight);
    let heightDiff = (Math.abs(window.outerHeight - newCanvasHeight)) / 2;
    let newContainerHeight = window.outerHeight + heightDiff;
    document.getElementById('content-container').style.height = newContainerHeight + "px";
  }
  this.mesh.material.uniforms.u_resolution.value = new THREE.Vector2(
    document.body.clientWidth,
    window.outerHeight
  );
  this.mesh.material.uniforms.u_resolution.needsUpdate = true;
  for (var i = 0; i < this.textures.length; i++) {
    var texture = this.textures[i];
    if (texture.image) {
      this.calculateAspectRatioFactor(i, texture);
    }
  }
  for (var j = 0; j < this.texturesInverse.length; j++) {
    var textureInverse = this.texturesInverse[j];
    if (textureInverse.image) {
      this.calculateAspectRatioFactor(i, textureInverse);
    }
  }
  this.render();
};
GLManager.prototype.scheduleLoop = function () {
  if (this.loopRaf) return;
  this.loop();
};

GLManager.prototype.loop = function () {
  this.render();
  this.time += 0.1;
  this.mesh.material.uniforms.u_time.value = this.time;

  this.loopRaf = requestAnimationFrame(this.loop);
};

GLManager.prototype.cancelLoop = function () {
  cancelAnimationFrame(this.loopRaf);
  this.loopRaf = null;
};

export {
  GLManager
};